import { useState } from 'react';

/** Room the chart gives up to the docked panel: its width (240 / 40) plus a 12px gap. */
const PANEL_GUTTER = 252;
const PANEL_GUTTER_COLLAPSED = 52;

/**
 * Wires an org chart to its unassigned-positions panel.
 *
 * Four charts share this — department, cost centre, the event-wide overview and
 * the venue chart — and they differ only in which endpoints they talk to. The
 * venue chart writes a different hierarchy entirely (`position_venue`), which
 * is why the update URL is a parameter rather than assumed.
 *
 * Everything here hangs off `unassignedUrl` being set. That is what keeps "off
 * the canvas" and "in the panel" true together: the tree is only asked to
 * exclude unassigned positions when there is a panel to show them in, so a
 * surface without one (or a Blade view missing the global) keeps showing them
 * rather than hiding them with nowhere to look.
 *
 * @param {Object}   options
 * @param {string}   options.unassignedUrl  Endpoint for the panel; falsy disables the whole feature.
 * @param {string}   options.treeUpdateUrl  Endpoint that persists a reporting line.
 * @param {Function} options.onAssigned     Called after a successful assignment, to reload the chart.
 *
 * Both drag directions run through `assign`, so both reload both sides.
 */
export default function useUnassignedPanel({ unassignedUrl, treeUpdateUrl, onAssigned }) {
    const [refreshToken, setRefreshToken] = useState(0);
    const [pendingId, setPendingId] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [count, setCount] = useState(0);
    /** The chart card currently being dragged off the canvas, if any. */
    const [draggedOffChartId, setDraggedOffChartId] = useState(null);

    const enabled = Boolean(unassignedUrl);

    const refreshPanel = () => setRefreshToken((token) => token + 1);

    /**
     * Persist a position dragged in from the panel, then reload both sides. The
     * rejection is left to propagate: the chart owns the inline error.
     */
    const assign = (positionId, supervisorId) => {
        setPendingId(positionId);

        return axios
            .put(treeUpdateUrl, { id: positionId, pid: supervisorId })
            .then(() => {
                onAssigned();
                refreshPanel();
            })
            .finally(() => {
                setPendingId(null);
            });
    };

    /**
     * Send a placed position back to the panel. The same endpoint and the same
     * reload as an assignment — only the supervisor it writes differs — so the
     * two directions cannot drift apart.
     */
    const unassign = (positionId) => assign(positionId, null);

    return {
        enabled,
        /** Space the chart column must leave for the panel beside it. */
        gutter: enabled ? (collapsed ? PANEL_GUTTER_COLLAPSED : PANEL_GUTTER) : 0,
        /** Spread onto the tree request's params. */
        treeParams: enabled ? { exclude_unassigned: 1 } : {},
        chartProps: {
            acceptsExternalDrops: enabled,
            unassignedCount: count,
            onExternalDrop: assign,
            // A drag within the chart can leave the position it moved away from
            // with neither a supervisor nor a subordinate, which puts it in the panel.
            onTreeUpdated: refreshPanel,
            onDragOffChart: enabled ? setDraggedOffChartId : undefined,
        },
        panelProps: {
            fetchUrl: unassignedUrl,
            refreshToken,
            pendingId,
            collapsed,
            onToggle: setCollapsed,
            onCountChange: setCount,
            // Row-onto-row: the only way to start a chart in a scope where
            // every position is unassigned and the canvas has nothing to aim at.
            onAssign: assign,
            // Card-onto-panel, the other direction: the chart says which card
            // has left the canvas, the panel reads where it is released.
            incomingFromChartId: draggedOffChartId,
            onUnassign: unassign,
        },
    };
}
